/**
 * Report links are opened by the PDF viewer and by plain links, which cannot send the sign-in ticket
 * as a header, so the ticket used to ride in the address. A report token is a short-lived stand-in
 * (POST /api/report_token, valid ten minutes, signed by the server): the long-lived ticket then never
 * appears in a URL, a server log or the browser history. Where the server does not know the endpoint
 * (an older backend) withDevice falls back to the ticket, so nothing breaks during a rollout.
 */
import axios from 'axios'
import { API } from '@/config'

const REFRESH_EVERY_MS = 5 * 60 * 1000
/** a token this close to its end is not handed out */
const MARGIN_MS = 60 * 1000

let current: { value: string; expiresAt: number } | null = null
let pending: Promise<void> | null = null
let timer: ReturnType<typeof setInterval> | null = null

/** The token to put in a report address, or null when there is none (or it is about to run out). */
export function reportToken(): string | null {
  return current && current.expiresAt - Date.now() > MARGIN_MS ? current.value : null
}

/** Gets a fresh token. Never throws: a failure just leaves the ticket fallback in place. */
export function refreshReportToken(timeoutMs = 4000): Promise<void> {
  if (pending) return pending
  pending = (async () => {
    try {
      const api = axios.create({ baseURL: API, timeout: timeoutMs })
      const response = await api.post('/report_token')
      const { token, expires_in } = response.data ?? {}
      current = token ? { value: String(token), expiresAt: Date.now() + Number(expires_in || 600) * 1000 } : null
    } catch {
      current = null
    } finally {
      pending = null
    }
  })()
  return pending
}

/** Keeps a token fresh while someone is signed in. Safe to call again. */
export function startReportTokens(): void {
  if (timer) return
  timer = setInterval(() => {
    if (hasTicket()) void refreshReportToken()
  }, REFRESH_EVERY_MS)
}

export function stopReportTokens(): void {
  if (timer) clearInterval(timer)
  timer = null
  current = null
}

function hasTicket(): boolean {
  try {
    return !!localStorage.getItem('authTicket')
  } catch {
    return false
  }
}

/** Before the first screen: get a token when there is a sign-in to get one for (at most a few seconds). */
export async function primeReportToken(): Promise<void> {
  if (!hasTicket()) return
  await refreshReportToken(2500)
  startReportTokens()
}
